import React, { useState } from 'react';
import { Copy, KeyRound, Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { api, formatApiError } from '../../lib/api';
import { useT } from '../../context/LanguageContext';

const AuthenticatorManager = ({ user, onUpdated }) => {
  const { t } = useT();
  const [setup, setSetup] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const beginSetup = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/auth/totp/setup');
      setSetup(data);
      setCode('');
    } catch (requestError) {
      toast.error(formatApiError(requestError, t('Kunne ikkje starte oppsettet.')));
    } finally {
      setBusy(false);
    }
  };

  const enable = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/auth/totp/enable', { code });
      onUpdated({ ...user, ...data.user, has_totp: true });
      setRecoveryCodes(data.recovery_codes || []);
      setSetup(null);
      setCode('');
      toast.success(t('Authenticator-appen er aktivert.'));
    } catch (requestError) {
      setError(formatApiError(requestError, t('Koden kunne ikkje verifiserast.')));
    } finally {
      setBusy(false);
    }
  };

  const disable = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/auth/totp/disable', { code });
      onUpdated({ ...user, ...data.user, has_totp: false });
      setCode('');
      setRecoveryCodes([]);
      toast.success(t('Authenticator-appen er deaktivert.'));
    } catch (requestError) {
      setError(formatApiError(requestError, t('Koden kunne ikkje verifiserast.')));
    } finally {
      setBusy(false);
    }
  };

  const copyRecoveryCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      toast.success(t('Kodene er kopierte.'));
    } catch {
      toast.error(t('Klarte ikkje kopiere kodane. Marker og kopier dei manuelt.'));
    }
  };

  return (
    <div className="space-y-4" data-testid="authenticator-manager">
      <p className="text-sm text-white/55">
        {user.has_totp
          ? t('Authenticator-appen krev ein eingongskode etter passord eller passnøkkel ved innlogging.')
          : t('Legg til eingongskodar frå Google Authenticator, Microsoft Authenticator, 1Password eller ei anna TOTP-app.')}
      </p>

      {!user.has_totp && !setup && (
        <button type="button" data-testid="authenticator-setup-button" onClick={beginSetup} disabled={busy} className="inline-flex items-center gap-2 rounded-full border-2 border-[#ff2e2e] px-7 py-3.5 font-bold uppercase tracking-wide text-[#ff2e2e] transition-colors hover:bg-[#ff2e2e] hover:text-black disabled:opacity-60">
          <Smartphone size={17} /> {busy ? t('Startar…') : t('Set opp authenticator-app')}
        </button>
      )}

      {setup && (
        <div className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="authenticator-setup">
          <div className="flex justify-center rounded-xl bg-white p-4">
            <QRCodeSVG value={setup.otpauth_uri} size={184} level="M" title={t('QR-kode for authenticator-app')} />
          </div>
          <p className="text-sm text-white/70">{t('Skann QR-koden i authenticator-appen. Du kan òg skrive inn nøkkelen manuelt:')}</p>
          <code data-testid="authenticator-manual-secret" className="block break-all rounded-lg bg-black/40 px-3 py-2 font-mono text-sm tracking-wider text-white">{setup.secret}</code>
          <form onSubmit={enable} className="space-y-3">
            <label htmlFor="authenticator-setup-code" className="block text-xs font-bold uppercase tracking-wider text-white/70">{t('Skriv inn seks-sifra kode for å aktivere')}</label>
            <input id="authenticator-setup-code" data-testid="authenticator-setup-code" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-center text-xl tracking-[0.3em] text-white outline-none focus:border-[#ff2e2e]" placeholder="000000" />
            {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={busy || code.length !== 6} className="inline-flex items-center gap-2 rounded-full bg-[#ff2e2e] px-6 py-3 font-bold text-black disabled:opacity-60">
                <KeyRound size={16} /> {busy ? t('Kontrollerer…') : t('Bekreft og aktiver')}
              </button>
              <button type="button" disabled={busy} onClick={() => { setSetup(null); setCode(''); setError(''); }} className="rounded-full border border-white/20 px-6 py-3 font-semibold text-white/70 hover:text-white disabled:opacity-60">{t('Avbryt')}</button>
            </div>
          </form>
        </div>
      )}

      {user.has_totp && (
        <form onSubmit={disable} className="space-y-3">
          <label htmlFor="authenticator-disable-code" className="block text-xs font-bold uppercase tracking-wider text-white/70">{t('Authenticator-app er aktiv')}</label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input id="authenticator-disable-code" data-testid="authenticator-disable-code" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-center text-lg tracking-[0.3em] text-white outline-none focus:border-[#ff2e2e] sm:max-w-[220px]" placeholder="000000" />
            <button type="submit" disabled={busy || code.length !== 6} className="inline-flex items-center justify-center gap-2 rounded-full border border-red-400/40 px-6 py-3 font-bold text-red-300 hover:bg-red-400/10 disabled:opacity-60">{busy ? t('Kontrollerer…') : t('Deaktiver authenticator')}</button>
          </div>
          {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
        </form>
      )}

      {recoveryCodes.length > 0 && (
        <div className="space-y-3 rounded-2xl border border-amber-400/25 bg-amber-400/5 p-5" data-testid="totp-recovery-codes">
          <div>
            <p className="font-bold text-amber-200">{t('Lagre gjenopprettingskodene no')}</p>
            <p className="mt-1 text-sm text-white/60">{t('Kvar kode kan brukast éin gong. Dei kan ikkje visast igjen.')}</p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {recoveryCodes.map((recoveryCode) => <code key={recoveryCode} className="rounded-lg bg-black/35 px-2 py-2 text-center font-mono text-sm text-white">{recoveryCode}</code>)}
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={copyRecoveryCodes} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white/80 hover:bg-white/10">
              <Copy size={15} /> {t('Kopier kodar')}
            </button>
            <button type="button" onClick={() => setRecoveryCodes([])} className="rounded-full px-5 py-2.5 text-sm font-semibold text-white/60 hover:text-white">{t('Eg har lagra kodane')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthenticatorManager;